// General.tsx

import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { equalProductStock } from '../../../../../../../../../../crud/management_api/stock_management';
import { ProductStock } from '../../../../../../types';
import { toast } from 'react-toastify';
import { HiXMark, HiArrowsRightLeft, HiArrowPath, HiExclamationTriangle } from 'react-icons/hi2';
import './EqualStock.scss';


// Propiedades del componente
interface GeneralProps {
  show: boolean; // Controla la visibilidad del modal
  setShow: (show: boolean) => void; // Función para cerrar el modal
  productStockData: ProductStock[]; // Datos del stock del producto
  setProductStockData: any;
  setOriginalProducts: any;
  currentEqualizeInfo: any;
  setCurrentEqualizeInfo: any;
}



const EqualStock: React.FC<GeneralProps> = ({ 
    show,
    setShow,
    productStockData,
    setProductStockData,
    setOriginalProducts,
    currentEqualizeInfo,
    setCurrentEqualizeInfo  
     }) => {

  // State for selected option and loading
  const [makeSystem, setMakeSystem] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (show) {
      setMakeSystem(null);
      setIsLoading(false);
    }
  }, [show]);

  const storageId: number | undefined = currentEqualizeInfo?.storageId ?? currentEqualizeInfo?.storage_unit_id;
  const baseItem = productStockData.find(
    (p: any) => p.product_id === currentEqualizeInfo?.product_id && p.storage_unit_id === storageId
  );

    // Always use the standard fields from the selected row
  const systemStock = Number(baseItem?.system_stock ?? currentEqualizeInfo?.system_stock ?? 0);
  const realStock = Number(baseItem?.reported_stock ?? 0);
  const potentialLoss = Math.max(0, systemStock - realStock);

  // Extract ingredient ID from product_id (remove 'in-' prefix)
  const extractIngredientId = (productId: string | number): number => {
    if (typeof productId === 'number') return productId;
    if (typeof productId === 'string' && productId.startsWith('in-')) {
      return parseInt(productId.replace('in-', ''));
    }
    return parseInt(String(productId));
  };

  const ingredientId: number | undefined = currentEqualizeInfo?.ingredient_id ?? 
    (currentEqualizeInfo?.product_id ? extractIngredientId(currentEqualizeInfo.product_id) : undefined);

  const confirmEqualizeStock = async () => {
    if (!currentEqualizeInfo || makeSystem === null || ingredientId === undefined || storageId === undefined) return;

    setIsLoading(true);
    try {
      // Correct parameter order: (ingredientId, storageUnitId, makeSystem)
      const response: any = await equalProductStock(ingredientId, storageId, makeSystem);
      const updatedProduct: ProductStock | undefined = response?.data;

      if (updatedProduct) {
        const updatedList = productStockData.map((item: any) =>
          ((item.product_id !== undefined && extractIngredientId(item.product_id) === ingredientId) ||
            (item.ingredient_id !== undefined && item.ingredient_id === ingredientId)) &&
          (item.storage_unit_id === storageId)
            ? updatedProduct
            : item
        );
        setProductStockData(updatedList);
        setOriginalProducts(updatedList);
      }

      if (makeSystem) {
        toast.success('Stock real igualado al stock en sistema correctamente.');
      } else {
        toast.success('Stock en sistema actualizado al stock real correctamente.');
      }
    } catch (error: any) {
      console.error('Error al procesar la igualación/actualización de stock:', error);
      toast.error('Error al procesar la solicitud.');
    } finally {
      setShow(false);
      setCurrentEqualizeInfo(null);
      setMakeSystem(null);
      setIsLoading(false);
    }
  };

  // Handle option selection (no API call here)
  const handleOptionSelect = (optionMakeSystem: boolean) => {
    setMakeSystem(optionMakeSystem);
  };

  const ChoiceCard: React.FC<{
    title: string;
    subtitle: string;
    selected: boolean;
    onClick: () => void;
    icon: React.ReactNode;
    warning?: string | null;
    disabled?: boolean;
  }> = ({ title, subtitle, selected, onClick, icon, warning, disabled = false }) => (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`choice-card ${selected ? 'selected' : ''} ${disabled ? 'disabled' : ''}`}
    >
      <div className="choice-card-icon">
        {icon}
      </div>
      <div className="choice-card-text">
        <div className="title">{title}</div>
        <div className="subtitle">{subtitle}</div>
        {!!warning && (
          <div className="warning">
            <HiExclamationTriangle size={16} /> {warning}
          </div>
        )}
      </div>
      <div aria-hidden className={`choice-card-radio ${selected ? 'selected' : ''}`} />
    </button>
  );

  const renderBody = () => {
    return (
      <div className="choice-grid">
        <ChoiceCard
          title="Igualar stock real al stock en sistema"
          subtitle={`El stock real quedará en ${systemStock}`}
          selected={makeSystem === true}
          onClick={() => handleOptionSelect(true)}
          icon={<HiArrowPath />}
          disabled={isLoading}
        />
        <ChoiceCard
          title="Actualizar stock en sistema al stock real"
          subtitle={`El stock en sistema quedará en ${realStock}`}
          selected={makeSystem === false}
          onClick={() => handleOptionSelect(false)}
          icon={<HiArrowsRightLeft />}
          warning={realStock < systemStock ? `Una pérdida de ${potentialLoss} será registrada en el sistema` : null}
          disabled={isLoading}
        />
      </div>
    );
  };

  return (
      <Modal
        show={show}
        onHide={() => setShow(false)}
        centered
        dialogClassName="mp-modal"
      >
        <Modal.Header className="mp-modal-header">
          <div className="form-modal-title-container">
            <span className="form-modal-title">Confirmar actualización</span>
          </div>
          <button className="mp-modal-close" onClick={() => setShow(false)}>
            <HiXMark size={20} />
          </button>
        </Modal.Header>
        <Modal.Body className="equal-stock-body">
          <div className="equal-stock-container">
            {renderBody()}
          </div>
        </Modal.Body>
        <Modal.Footer className="mp-modal-footer">
          <button
            className="mp-modal-btn-cancel"
            onClick={() => setShow(false)}
            disabled={isLoading}
          >
            Cancelar
          </button>
          <button
            className="mp-modal-btn-submit"
            onClick={confirmEqualizeStock}
            disabled={makeSystem === null || isLoading}
          >
            {isLoading ? 'Procesando...' : 'Confirmar'}
          </button>
        </Modal.Footer>
      </Modal>
  );
};

export default EqualStock;
